import {useMemo, useState} from "react";
import {VehicleEntity} from "@ots-frontend/ots-data";
import {useDebouncedValue} from "@mantine/hooks";
import {
  DataTableFilterConfig,
  useDataTableFilter,
  UseDataTableFilterProps,
  UseDataTableFilterResult
} from "../DataTable/useDataTableFilter";
import {useTranslation} from "react-i18next";

export type UseVehicleDataTableFilterProps = Omit<UseDataTableFilterProps<VehicleEntity>, 'filterConfig'>

export function useVehicleDataTableFilter(props: UseVehicleDataTableFilterProps): UseDataTableFilterResult<VehicleEntity> {
  const {records} = props;

  const {t} = useTranslation(["common"])

  const [nameQuery, setNameQuery] = useState<string | undefined>(undefined);
  const [nameDebouncedQuery] = useDebouncedValue(nameQuery, 200);

  const [barcodeQuery, setBarcodeQuery] = useState<string | undefined>(undefined);
  const [barcodeDebouncedQuery] = useDebouncedValue(barcodeQuery, 200);

  const [companyQuery, setCompanyQuery] = useState<string | undefined>(undefined);
  const [companyDebouncedQuery] = useDebouncedValue(companyQuery, 200);

  const filterConfig: DataTableFilterConfig<VehicleEntity> = useMemo(() => {
    return {
      "name": {
        setQuery: setNameQuery,
        query: nameQuery,
        debouncedQuery: nameDebouncedQuery,
        filter: (record, query) => !record.name.toLowerCase().includes(query.toLowerCase())
      },
      "barcode": {
        setQuery: setBarcodeQuery,
        query: barcodeQuery,
        debouncedQuery: barcodeDebouncedQuery,
        filter: ({barcode}, query) => !barcode || !barcode.toLowerCase().includes(query.toLowerCase())
      },
      "company": {
        setQuery: setCompanyQuery,
        query: companyQuery,
        debouncedQuery: companyDebouncedQuery,
        filter: ({companyName}, query) => !companyName || !companyName.toLowerCase().includes(query.toLowerCase())
      },
    }
  }, [
    nameQuery,
    nameDebouncedQuery,
    barcodeQuery,
    barcodeDebouncedQuery,
    companyQuery,
    companyDebouncedQuery,
    t
  ])

  const {filteredRecords} = useDataTableFilter({
    records: records,
    filterConfig: filterConfig,
  })

  return {
    filteredRecords: filteredRecords,
    filterConfig: filterConfig
  };
}
